/**
 * On-screen keyboard bridge for the engine's in-canvas text prompts.
 *
 * Naming a save, or a character, puts the engine into a loop that draws its own
 * text field into the framebuffer and reads raw key events. None of that is
 * DOM, so on a phone nothing is focused, no OS keyboard appears, and the prompt
 * cannot be answered at all.
 *
 * This overlay closes that gap without the engine knowing about it. The engine
 * publishes "a text prompt is open" and the field's initial contents through
 * its `rr_` bridge; we poll that, and while it is true under touch input we
 * show a real <input> pinned to the bottom of the *visible* viewport, which is
 * the strip directly above the OS keyboard once it has slid up.
 *
 * The typed text is never sent to the engine as text, and it is never sent as a
 * forged KeyboardEvent either: synthetic in-page events do not reliably reach
 * Emscripten's SDL2 listeners, which is why the earlier version of this file
 * typed nothing at all into the engine's save-name field. Instead we diff each
 * `input` event against a mirror of what the engine already has and replay the
 * difference through `_rr_key_event`, which enqueues one logical GNW key
 * straight into the queue the prompt loop reads from. Logical codes are
 * printable ASCII 32..122 as themselves — the code carries its own case, so
 * there is no shift state to wrap — plus 8 Backspace, 13 Return, 27 Escape.
 */

/** Poll period for the engine's active flag. Fast enough that the bar appears
 *  with the prompt, cheap enough to leave running for the whole session. */
const POLL_MS = 150;

/** One logical key per tick. The engine's prompt loop dequeues one entry per
 *  ~24Hz iteration from a 40-slot queue, and every character now costs exactly
 *  one entry (no shift wrapping). Pacing at ~16 keys a second stays under the
 *  24/s consumption rate, so no burst — including a select-all delete and
 *  retype — can outrun the engine and silently drop keys. */
const DRAIN_MS = 60;

/** LOAD_SAVE_DESCRIPTION_LENGTH is 30 in engine/src/loadsave.cc, including the
 *  terminating NUL. Character names are capped lower engine-side; the prompt
 *  loops simply stop appending at their own maximum, so anything past it is
 *  harmlessly ignored rather than mis-typed. */
const MAX_LENGTH = 29;

/** Logical key codes the engine understands outside the printable range. */
const KEY_BACKSPACE = 8;
const KEY_RETURN = 13;
const KEY_ESCAPE = 27;

/** Inclusive bounds of the printable range the engine passes through as-is. */
const PRINTABLE_MIN = 32;
const PRINTABLE_MAX = 122;

export interface TextEntryBridge {
  destroy(): void;
}

export function mountTextEntry(opts: {
  /** True while the engine has a text prompt open. */
  active: () => boolean;
  /** The engine field's initial contents at the moment the prompt opened. */
  prefill: () => string;
  /** True when the last input modality was touch-like. */
  isTouch: () => boolean;
  /** Hands one logical key event to the engine's prompt queue. Returns the
   *  engine's status code (0 accepted, -1 rejected, -99 export missing). */
  sendKey: (logicalKey: number) => number;
}): TextEntryBridge {
  const bar = document.createElement('div');
  bar.id = 'rr-text-entry';
  bar.hidden = true;

  const input = document.createElement('input');
  input.id = 'rr-text-entry-field';
  input.type = 'text';
  input.autocapitalize = 'off';
  input.autocomplete = 'off';
  input.spellcheck = false;
  input.enterKeyHint = 'done';
  input.maxLength = MAX_LENGTH;
  input.placeholder = 'Save name';

  const done = document.createElement('button');
  done.type = 'button';
  done.className = 'primary';
  done.textContent = 'Done';

  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'ghost';
  cancel.textContent = 'Cancel';

  bar.append(input, done, cancel);

  // Diagnostic strip, only under the ?hitboxes debug mode that also drives the
  // touch overlay. It answers, from a single screen recording: did focus stay
  // on the field, did the DOM ever receive the typed text, and what did the
  // engine say to each injected key.
  const debugEnabled = new URLSearchParams(location.search).has('hitboxes');
  const debugLine = document.createElement('span');
  debugLine.id = 'rr-text-entry-debug';
  let debugKeydowns = 0;
  let debugInputs = 0;
  let debugSent = 0;
  let debugLastRc = 99;
  let debugTimer: number | null = null;
  const debugRefresh = (): void => {
    if (!debugEnabled || bar.hidden) return;
    const focused = document.activeElement;
    const focusName = focused === input ? 'FIELD' : (focused ? `${focused.tagName}#${focused.id || '?'}` : 'NONE');
    debugLine.textContent = `foc=${focusName} kd=${debugKeydowns} in=${debugInputs} val="${input.value}" mir="${mirror}" q=${queue.length} sent=${debugSent} rc=${debugLastRc}`;
  };
  if (debugEnabled) {
    bar.append(debugLine);
    debugTimer = window.setInterval(debugRefresh, 250);
  }
  document.body.append(bar);

  // What the engine's own field currently holds, as far as we know: every key
  // we send is applied to this string too, so the next diff is against the
  // engine's state rather than against the previous DOM value.
  let mirror = '';
  let queue: number[] = [];
  let drainTimer: number | null = null;
  // Set by commit() when the queue still has typing in it; the drain fires the
  // Return only once the field content has actually landed in the engine.
  let commitPending = false;

  // The OS keyboard does not resize the layout viewport on iOS, it only shrinks
  // the visual one, so `bottom: 0` would leave the bar underneath the keyboard.
  // Following visualViewport puts it in the strip that is actually on screen.
  const viewport = window.visualViewport;

  const reposition = (): void => {
    if (!viewport || bar.hidden) return;
    // iOS ignores focus({preventScroll}) for the scroll it performs when the
    // keyboard rises: it shoves the whole page up to "reveal" the focused
    // input, which drags the game canvas - and the engine's own prompt field -
    // clean off screen. The app is a fixed full-viewport layout with nothing
    // to scroll, so pinning the page back to origin is always correct here.
    if (window.scrollY !== 0 || window.scrollX !== 0) {
      window.scrollTo(0, 0);
    }
    const top = viewport.offsetTop + viewport.height - bar.offsetHeight;
    bar.style.top = `${Math.max(0, top)}px`;
    bar.style.bottom = 'auto';
  };

  if (viewport) {
    // Left mounted for the session: both handlers are a couple of reads and a
    // style write, and only while the bar is visible.
    viewport.addEventListener('resize', reposition);
    viewport.addEventListener('scroll', reposition);
  }

  const stopDrain = (): void => {
    if (drainTimer !== null) {
      clearInterval(drainTimer);
      drainTimer = null;
    }
  };

  const drain = (): void => {
    const key = queue.shift();
    if (key === undefined) {
      stopDrain();
      if (commitPending) {
        commitPending = false;
        opts.sendKey(KEY_RETURN);
        input.blur();
      }
      return;
    }
    debugSent++;
    debugLastRc = opts.sendKey(key);
  };

  const startDrain = (): void => {
    if (drainTimer === null) drainTimer = window.setInterval(drain, DRAIN_MS);
  };

  const enqueue = (keys: number[]): void => {
    if (keys.length === 0) return;
    queue.push(...keys);
    startDrain();
  };

  /** Drop anything outside the range the engine passes through, so the mirror
   *  can never contain a character we are unable to replay or later erase. */
  const sanitize = (value: string): string => {
    let out = '';
    for (const ch of value) {
      const code = ch.charCodeAt(0);
      if (ch.length === 1 && code >= PRINTABLE_MIN && code <= PRINTABLE_MAX) out += ch;
    }
    return out.slice(0, MAX_LENGTH);
  };

  const onInput = (): void => {
    debugInputs++;
    const value = sanitize(input.value);
    if (value !== input.value) {
      // Rewriting the value collapses the selection to the end, which is where
      // the caret already is for ordinary typing.
      input.value = value;
    }
    if (value === mirror) return;

    let prefix = 0;
    while (prefix < mirror.length && prefix < value.length && mirror[prefix] === value[prefix]) prefix++;

    const keys: number[] = [];
    for (let i = 0; i < mirror.length - prefix; i++) keys.push(KEY_BACKSPACE);
    for (const ch of value.slice(prefix)) keys.push(ch.charCodeAt(0));
    mirror = value;
    enqueue(keys);
  };

  const commit = (): void => {
    if (queue.length > 0 || drainTimer !== null) {
      // Return must arrive after the last character, not alongside it.
      commitPending = true;
      startDrain();
      return;
    }
    opts.sendKey(KEY_RETURN);
    input.blur();
    // Deliberately not hidden here: the falling edge of the engine's own active
    // flag is the only thing that proves the prompt closed. If the engine
    // rejects the entry the bar stays up and the player can keep editing.
  };

  const cancelEntry = (): void => {
    queue = [];
    commitPending = false;
    stopDrain();
    opts.sendKey(KEY_ESCAPE);
    input.blur();
  };

  input.addEventListener('keydown', () => { debugKeydowns++; });
  // Emscripten SDL2 registers a window-level `keypress` handler that returns
  // "consume" whenever SDL text input is active - which is precisely while the
  // engine's prompt is open, because the prompt calls SDL_StartTextInput. A
  // consumed keypress is preventDefault'ed, and inserting the character into a
  // DOM input IS keypress's default action - so every typed character died
  // while autofill (which never fires keypress) sailed through. Keep the whole
  // key event family inside the bar.
  input.addEventListener('keypress', (event) => event.stopPropagation());
  input.addEventListener('keyup', (event) => event.stopPropagation());
  input.addEventListener('keydown', (event) => {
    // A hardware or Bluetooth keyboard would otherwise reach SDL's own window
    // listener as well as our diff-and-replay path, and every character would
    // be typed twice. The replayed path is the single source of truth.
    event.stopPropagation();
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelEntry();
    }
  });
  input.addEventListener('input', onInput);

  // pointerdown rather than click: the default action of a press on a button is
  // to move focus off the input, which would dismiss the keyboard and, on iOS,
  // fire a scroll before the handler ever runs.
  done.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    commit();
  });
  cancel.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    cancelEntry();
  });

  const show = (): void => {
    const initial = sanitize(opts.prefill());
    mirror = initial;
    input.value = initial;
    bar.hidden = false;
    reposition();
    // Android raises the keyboard on programmatic focus; iOS requires a user
    // gesture, which is why the bar has to be visible and obviously a text
    // field — the player taps it and gets the keyboard that way.
    input.focus({ preventScroll: true });
  };

  const hide = (): void => {
    queue = [];
    commitPending = false;
    stopDrain();
    mirror = '';
    input.value = '';
    input.blur();
    bar.hidden = true;
    // Cleared so the stylesheet's own placement applies again next time, until
    // the first reposition() of the next prompt takes it over.
    bar.style.top = '';
    bar.style.bottom = '';
  };

  let shown = false;
  const poll = window.setInterval(() => {
    const active = opts.active();
    if (active === shown) return;
    // Only the touch case needs this. With a physical keyboard the engine's
    // prompt already works, and an input bar stealing focus would break it.
    if (active && !opts.isTouch()) return;
    shown = active;
    if (active) show();
    else hide();
  }, POLL_MS);

  return {
    destroy(): void {
      clearInterval(poll);
      if (debugTimer !== null) clearInterval(debugTimer);
      stopDrain();
      if (viewport) {
        viewport.removeEventListener('resize', reposition);
        viewport.removeEventListener('scroll', reposition);
      }
      bar.remove();
    },
  };
}
